import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Package, MapPin, CreditCard, Download, ArrowRight, Truck, ShoppingBag, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ReviewModal } from '../components/ReviewModal';
import { Star } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, product: null, orderId: null });

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/orders/${orderId}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setOrder(data);
        } else {
          toast.error('Could not load order details');
        }
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    window.scrollTo(0, 0);
  }, [orderId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/60';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-body animate-pulse">Confirming your order...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 md:py-20 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px]">
      <div className="max-w-4xl mx-auto px-4">

        {/* Success Branding Section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="w-24 h-24 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto ring-8 ring-emerald-500/5 rotate-12 transition-transform hover:rotate-0 duration-500">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            </div>
            {/* Decorative particles (simplified for React) */}
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-emerald-400 rounded-full animate-ping opacity-20" />
            <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-blue-400 rounded-full animate-bounce opacity-20" />
          </div>

          <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mb-4 tracking-tight">
            Order <span className="text-primary italic">Confirmed!</span>
          </h1>
          <p className="text-slate-500 font-body text-lg max-w-lg mx-auto">
            Woot! Your style upgrade is on its way. We've sent a confirmation to your email.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* Main Details Area */}
          <div className="lg:col-span-2 space-y-6 animate-slide-up">

            {/* Primary Order Info Card */}
            <div className="bg-white/80 backdrop-blur-xl border border-white rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="p-8">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                  <div>
                    <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Order Identifier</h2>
                    <p className="font-heading text-2xl text-slate-900">#{order?.order_id}</p>
                  </div>
                  <div className="bg-emerald-50 text-emerald-700 px-4 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 uppercase tracking-wide border border-emerald-100">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    Processing
                  </div>
                </div>

                <div className="space-y-6">
                  {order?.items?.map((item, idx) => (
                    <div key={idx} className="group flex items-center gap-6 p-4 rounded-3xl hover:bg-slate-50 transition-colors duration-300">
                      <div className="relative flex-shrink-0 w-20 h-20 bg-white border border-slate-100 rounded-2xl flex items-center justify-center overflow-hidden">
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          className="max-w-full max-h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute -top-2 -right-2 bg-slate-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white">
                          {item.quantity}
                        </span>
                      </div>
                       <div className="flex-1 min-w-0">
                        <h3 className="font-body font-semibold text-slate-900 truncate group-hover:text-primary transition-colors">{item.name}</h3>
                        <p className="text-sm text-slate-400 font-body mt-0.5">Premium Quality Assurance</p>
                        
                        {order?.status === 'delivered' && (
                          <button 
                            onClick={() => setReviewModal({ open: true, product: { product_id: item.product_id, name: item.name }, orderId: order.order_id })}
                            className="mt-3 flex items-center gap-1.5 text-[10px] font-bold text-primary hover:text-primary/80 uppercase tracking-widest group"
                          >
                            <Star className="w-3 h-3 group-hover:fill-primary" />
                            Write a Review
                          </button>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-lg text-slate-900">{formatPrice(item.item_total)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions Footer */}
              <div className="bg-slate-50/50 border-t border-slate-100 p-6 flex flex-wrap gap-4 justify-between">
                <Button asChild variant="ghost" className="rounded-2xl h-12 px-6 font-body text-slate-600 hover:bg-white hover:text-primary">
                  <Link to="/dashboard/orders" className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Manage Orders
                  </Link>
                </Button>

                <Button asChild className="rounded-2xl h-12 px-8 font-body bg-slate-900 hover:bg-slate-800 text-white group shadow-lg shadow-slate-200">
                  <Link to={`/track-order?orderId=${order?.order_id}`} className="flex items-center gap-2">
                    <Truck className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    Track Shipment
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Address & Payment Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Delivery Address */}
              <div className="bg-white/80 backdrop-blur-xl border border-white rounded-[2rem] p-7 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-heading text-lg text-slate-900">Delivery</h3>
                </div>
                {order?.shipping_address && (
                  <div className="space-y-1">
                    <p className="font-body font-bold text-slate-900">{order.shipping_address.full_name}</p>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {order.shipping_address.address_line1}, {order.shipping_address.city}<br />
                      {order.shipping_address.state} - {order.shipping_address.pincode}
                    </p>
                    <p className="text-xs text-slate-400 font-body pt-2">Contact: {order.shipping_address.phone}</p>
                  </div>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-white/80 backdrop-blur-xl border border-white rounded-[2rem] p-7 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="font-heading text-lg text-slate-900">Payment</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400 uppercase text-[10px] font-bold tracking-wider">Method</span>
                    <span className="font-body font-bold text-slate-900 capitalize">{order?.payment_method}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400 uppercase text-[10px] font-bold tracking-wider">Status</span>
                    <span className={`font-bold px-2.5 py-0.5 rounded-lg text-[10px] uppercase border ${order?.payment_status?.toLowerCase() === 'paid'
                      ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                      : 'bg-orange-50 text-orange-600 border-orange-100'
                      }`}>
                      {order?.payment_status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Summary Area */}
          <div className="lg:col-span-1 space-y-6 animate-slide-up [animation-delay:200ms]">
            <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 shadow-2xl shadow-slate-200 relative overflow-hidden group">
              {/* Decorative accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 transition-transform duration-1000 group-hover:scale-125" />

              <h3 className="font-heading text-xl mb-8 relative z-10 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-primary" />
                Receipt Summary
              </h3>

              <div className="space-y-4 relative z-10">
                <div className="flex justify-between text-slate-400 text-sm">
                  <span>Subtotal</span>
                  <span className="text-white font-medium">{formatPrice(order?.subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-400 text-sm">
                  <span>Shipping</span>
                  <span className="text-emerald-400 font-medium">FREE</span>
                </div>
                {order?.discount > 0 && (
                  <div className="flex justify-between text-primary text-sm">
                    <span>Discount</span>
                    <span>-{formatPrice(order.discount)}</span>
                  </div>
                )}
                <div className="pt-6 mt-6 border-t border-white/10 flex justify-between items-end">
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Total Paid</p>
                    <p className="text-3xl font-heading text-white">{formatPrice(order?.total)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Card */}
            <div className="bg-primary/5 border border-primary/10 rounded-[2rem] p-6 text-center group transition-all duration-300 hover:bg-primary/10">
              <p className="text-sm text-slate-600 font-body mb-4 leading-relaxed px-2">
                Need help with your order? Our team is always ready to assist.
              </p>
              <a
                href="mailto:support@nso-india.com"
                className="inline-flex items-center gap-2 text-primary text-sm font-bold hover:gap-3 transition-all"
              >
                Connect with Support
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <Button asChild variant="outline" className="w-full rounded-[1.5rem] h-14 font-body border-slate-200 hover:bg-slate-50 text-slate-600">
              <Link to="/">Continue Shopping</Link>
            </Button>
          </div>

        </div>

        {/* Footer info */}
        <div className="mt-16 text-center text-slate-400 text-sm font-body">
          <p>© {new Date().getFullYear()} Nso-India. All rights reserved.</p>
        </div>
      </div>

      {reviewModal.open && (
        <ReviewModal
          isOpen={reviewModal.open}
          onClose={() => setReviewModal({ open: false, product: null, orderId: null })}
          product={reviewModal.product}
          orderId={reviewModal.orderId}
        />
      )}
    </div>
  );
}
